// DocumentItemsColumnHeader.jsx

import React, { useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './DocumentItemsColumnHeader.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Popup from 'components/popup/Popup';
import PopupOption from 'components/popup/option/PopupOption';

/**
 * DocumentItemsColumnHeader
 *
 * Shared items-table column header with optional client-visibility cue and toggle menu
 * (Proposal / Invoice).
 *
 * @param {string} label - Column header label.
 * @param {string} [className] - Optional th className.
 * @param {boolean} [clientVisible=true] - Whether this column is shown to the client.
 * @param {boolean} [canToggleClientVisibility=false] - Show/hide menu when editable.
 * @param {function} [onClientVisibleChange] - `(nextVisible: boolean) => void`.
 * @returns {ReactNode}
 */
const DocumentItemsColumnHeader = React.memo(({
  label,
  className = '',
  clientVisible = true,
  canToggleClientVisibility = false,
  onClientVisibleChange = null,
}) => {
  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- REFS ----- //

  const anchorRef = useRef(null);

  // ----- STATE ----- //

  // Column menu open state
  const [menuOpen, setMenuOpen] = useState(false);

  const showHiddenCue = clientVisible === false;
  const interactive = Boolean(canToggleClientVisibility && typeof onClientVisibleChange === 'function');

  // ----- HANDLERS ----- //

  /**
   * Opens or closes the column header menu.
   *
   * @returns {void}
   */
  const handleToggleMenu = () => {
    if (!interactive) return;
    setMenuOpen((prev) => !prev);
  };

  /**
   * Sets client visibility and closes the menu.
   *
   * @param {boolean} nextVisible
   * @returns {void}
   */
  const handleSetClientVisible = (nextVisible) => {
    onClientVisibleChange?.(nextVisible);
    setMenuOpen(false);
  };

  // ----- RENDER ----- //

  const thClassName = [
    className,
    interactive ? 'document-items-col-header-interactive' : '',
  ].filter(Boolean).join(' ');

  return (
    <th className={thClassName || undefined} scope="col">
      {interactive ? (
        <>
          <button
            ref={anchorRef}
            type="button"
            className="document-items-col-header-trigger"
            style={{
              color: theme.foregroundMuted,
            }}
            title={showHiddenCue ? 'Hidden from client' : 'Visible to client'}
            onClick={handleToggleMenu}
          >
            {showHiddenCue ? (
              <Icon
                iconKey="visibility_off"
                width={14}
                height={14}
                color={theme.foregroundMuted}
              />
            ) : null}
            <span className="document-items-col-header-label">{label}</span>
            <Icon
              iconKey="keyboard_arrow_down"
              width={14}
              height={14}
              color={theme.foregroundMuted}
            />
          </button>

          <Popup
            anchorRef={anchorRef}
            isOpen={menuOpen}
            setOpen={setMenuOpen}
            placement="bottom"
            align="start"
          >
            <div className="document-items-col-header-menu">
              {showHiddenCue ? (
                <PopupOption
                  iconKey="visibility"
                  label="Show to client"
                  onClick={() => handleSetClientVisible(true)}
                />
              ) : (
                <PopupOption
                  iconKey="visibility_off"
                  label="Hide from client"
                  onClick={() => handleSetClientVisible(false)}
                />
              )}
            </div>
          </Popup>
        </>
      ) : (
        <span className="document-items-col-header-static">
          {showHiddenCue ? (
            <Icon
              iconKey="visibility_off"
              width={14}
              height={14}
              color={theme.foregroundMuted}
            />
          ) : null}
          <span className="document-items-col-header-label">{label}</span>
        </span>
      )}
    </th>
  );
});

// Dev Tools
DocumentItemsColumnHeader.displayName = 'DocumentItemsColumnHeader';

export default DocumentItemsColumnHeader;
